import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Box,
  Paper,
  IconButton,
  Tooltip,
  TextField
} from '@mui/material';
import { styled, createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  User,
  FileText,
  DollarSign,
  AlertTriangle,
  CheckCircle,
  XCircle,
  CreditCard,
  Building2,
  Search,
  RefreshCw,
  Eye,
  Users,
  Clock
} from 'lucide-react';
import axios from 'axios';
import ResponsiveAppBar from '../components/AppBar';
import { API_BASE_URL } from '../api/config';
import { matchCustomerSearch } from '../components/CustomerSearchBar';

const BASE_URL = `${API_BASE_URL}/velavan_finance/get_all_entries`;

// Create theme with teal/purple color scheme
const theme = createTheme({
  palette: {
    primary: { main: '#77c8c7' }, // Teal
    secondary: { main: '#925f96' }, // Purple
    background: { default: '#f8fafc' },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
  },
  typography: {
    fontFamily: '"Poppins", "Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '0.5px',
      background: 'linear-gradient(135deg, #77c8c7 0%, #925f96 100%)',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
    },
    subtitle1: {
      fontWeight: 500,
      color: '#64748b',
      fontSize: '1.1rem'
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
          transition: 'transform 0.2s, box-shadow 0.2s',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: '0 6px 16px rgba(119, 200, 199, 0.15)',
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          backgroundColor: '#fff',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#77c8c7',
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 12,
            background: '#ffffff',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
            transition: 'box-shadow 0.2s',
            '&:hover': {
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#77c8c7',
            },
          },
        },
      },
    },
  },
});

const StyledChip = styled(Chip)(({ theme, status }) => {
  const isClosed = status === 'closed/paid' || status === 'closed' || status === 'paid';
  const isActive = status === 'active';
  const isNpa = status === 'npa';

  return {
    backgroundColor:
      isActive ? 'rgba(119, 200, 199, 0.2)' :
      isClosed ? 'rgba(254, 226, 226, 0.9)' :
      isNpa ? 'rgba(254, 243, 199, 0.8)' : '#f1f5f9',
    color:
      isActive ? '#0f766e' :
      isClosed ? '#991b1b' :
      isNpa ? '#b45309' : '#475569',
    fontWeight: 700,
    textTransform: 'uppercase',
    borderRadius: 8,
    '& .MuiChip-icon': {
      color: 'inherit',
    },
  };
});

const StatsCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: 12,
  background: 'linear-gradient(135deg, rgba(119, 200, 199, 0.1) 0%, rgba(146, 95, 150, 0.1) 100%)',
  border: '1px solid rgba(119, 200, 199, 0.2)',
  transition: 'transform 0.2s, box-shadow 0.2s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 20px rgba(119, 200, 199, 0.15)',
  },
}));

const AccountStatusDashboard = () => {
  const [accounts, setAccounts] = useState([]);
  const [filteredStatus, setFilteredStatus] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(BASE_URL);
      if (!response.data) throw new Error('No data received');
      setAccounts(response.data.map(entry => ({ ...entry, id: entry.refNo })));
    } catch (err) {
      setError('Failed to fetch accounts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    const s = status ? status.toLowerCase() : '';
    switch (s) {
      case 'active':
        return <CheckCircle size={16} />;
      case 'closed/paid':
      case 'closed':
      case 'paid':
        return <XCircle size={16} />;
      case 'npa':
        return <AlertTriangle size={16} />;
      default:
        return <CreditCard size={16} />;
    }
  };

  const getAccountStats = () => {
    const total = accounts.length;
    const active = accounts.filter(acc => acc.accountStatus?.toLowerCase() === 'active').length;
    const closed = accounts.filter(acc => {
      const s = acc.accountStatus?.toLowerCase();
      return s === 'closed/paid' || s === 'closed' || s === 'paid';
    }).length;
    const npa = accounts.filter(acc => acc.accountStatus?.toLowerCase() === 'npa').length;

    const totalLended = accounts.reduce((sum, acc) => sum + (acc.lendedAmount || 0), 0);
    const totalPending = accounts.reduce((sum, acc) => sum + (acc.pendingDue || 0), 0);

    return { total, active, closed, npa, totalLended, totalPending };
  };

  const filteredAccounts = filteredStatus === 'All'
    ? accounts
    : accounts.filter(account => {
        const s = account.accountStatus?.toLowerCase();
        const f = filteredStatus.toLowerCase();
        if (f === 'closed/paid') {
          return s === 'closed/paid' || s === 'closed' || s === 'paid';
        }
        return s === f;
      });

  const searchedAccounts = filteredAccounts.filter(account => matchCustomerSearch(account, searchQuery));

  const stats = getAccountStats();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ResponsiveAppBar />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
            <Building2 size={32} color="#77c8c7" />
            <Typography variant="h4" component="h1">
              Account Status Dashboard
            </Typography>
          </Box>
          <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <Eye size={20} />
            Monitor and manage your financial accounts
          </Typography>
          <Divider sx={{ bgcolor: 'primary.light', height: 3, mx: 'auto', width: '100px', borderRadius: '2px', mt: 2 }} />
        </Box>

        {/* Stats Overview */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={2.4}>
            <StatsCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Users size={24} color="#77c8c7" />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {stats.total}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Accounts
                  </Typography>
                </Box>
              </Box>
            </StatsCard>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <StatsCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle size={24} color="#1a73e8" />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1a73e8' }}>
                    {stats.active}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active
                  </Typography>
                </Box>
              </Box>
            </StatsCard>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <StatsCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <XCircle size={24} color="#d93025" />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#d93025' }}>
                    {stats.closed}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Closed/Paid
                  </Typography>
                </Box>
              </Box>
            </StatsCard>
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <StatsCard>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AlertTriangle size={24} color="#f9a825" />
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#f9a825' }}>
                    {stats.npa}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    NPA
                  </Typography>
                </Box>
              </Box>
            </StatsCard>
          </Grid>

          {/* Additional Financial Stats Row */}
          <Grid item xs={12} sm={6} md={2.4}>
              <StatsCard>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                  <Clock size={24} color="#f59e0b" />
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#f59e0b' }}>
                 {stats.totalPending.toLocaleString()}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Total Pending 
                    </Typography>
                  </Box>
                </Box>
              </StatsCard>
            </Grid>
        </Grid>



        {/* Search and Filter Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search by Name or Ref No (e.g., VF-0001)"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ minWidth: 200, maxWidth: 300 }}
            InputProps={{
              startAdornment: (
                <Search size={20} color="#77c8c7" style={{ marginRight: 8 }} />
              ),
            }}
          />
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel id="status-filter-label">Account Status</InputLabel>
            <Select
              labelId="status-filter-label"
              value={filteredStatus}
              label="Account Status"
              onChange={(e) => setFilteredStatus(e.target.value)}
            >
              <MenuItem value="All">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CreditCard size={16} />
                  All Accounts
                </Box>
              </MenuItem>
              <MenuItem value="active">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle size={16} />
                  Active
                </Box>
              </MenuItem>
              <MenuItem value="closed/paid">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <XCircle size={16} />
                  Closed/Paid
                </Box>
              </MenuItem>
              <MenuItem value="npa">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AlertTriangle size={16} />
                  NPA
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Refresh Data">
            <IconButton onClick={fetchAccounts} color="primary" disabled={loading}>
              <RefreshCw size={20} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Loading and Error States */}
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, my: 4 }}>
            <CircularProgress size={24} />
            <Typography>Loading accounts...</Typography>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AlertTriangle size={20} />
            {error}
          </Alert>
        )}

        {/* Accounts Grid */}
        <Grid container spacing={3}>
          {searchedAccounts.map((account) => {
            const rawStatus = account.accountStatus?.toLowerCase();
            const isClosed = rawStatus === 'closed/paid' || rawStatus === 'closed' || rawStatus === 'paid';
            const isNpa = rawStatus === 'npa';

            return (
              <Grid item xs={12} sm={6} md={4} key={account.id}>
                <Card
                  sx={{
                    bgcolor: isClosed
                      ? 'rgba(254, 242, 242, 0.75)'
                      : isNpa
                        ? 'rgba(254, 243, 199, 0.35)'
                        : '#ffffff',
                    border: isClosed
                      ? '1px solid rgba(254, 202, 202, 0.9)'
                      : isNpa
                        ? '1px solid rgba(253, 230, 138, 0.9)'
                        : '1px solid rgba(226, 232, 240, 0.8)',
                    boxShadow: isClosed
                      ? '0 4px 14px rgba(239, 68, 68, 0.08)'
                      : '0 4px 12px rgba(0, 0, 0, 0.04)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      boxShadow: isClosed
                        ? '0 8px 20px rgba(239, 68, 68, 0.16)'
                        : '0 8px 20px rgba(119, 200, 199, 0.15)',
                    }
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <User size={20} color="#77c8c7" />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                          {account.name || 'N/A'}
                        </Typography>
                      </Box>
                      <StyledChip
                        icon={getStatusIcon(account.accountStatus)}
                        label={account.accountStatus}
                        status={account.accountStatus ? account.accountStatus.toLowerCase() : ''}
                      />
                    </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <FileText size={16} color="#64748b" />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Ref No: {account.refNo || 'N/A'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <DollarSign size={16} color="#10b981" />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Lended Amount: Rs. {account.lendedAmount?.toLocaleString() || '0.00'}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Clock size={16} color="#f59e0b" />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Pending Due: Rs. {account.pendingDue?.toLocaleString() || '0.00'}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
        </Grid>

        {/* No Accounts Message */}
        {!loading && searchedAccounts.length === 0 && !error && (
          <Box sx={{ textAlign: 'center', mt: 4, py: 4 }}>
            <Search size={48} color="#94a3b8" style={{ marginBottom: 16 }} />
            <Typography align="center" color="text.secondary" variant="h6">
              No accounts found for the selected criteria.
            </Typography>
            <Typography align="center" color="text.secondary" variant="body2">
              Try adjusting your filter or search query.
            </Typography>
          </Box>
        )}
      </Container>
    </ThemeProvider >
  );
};

export default AccountStatusDashboard;